import { useState } from "react";
import { motion } from "framer-motion";
import { Car, CalendarClock, Droplets, CreditCard, CheckCircle2, Loader2, ChevronDown, Paperclip } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { OilTrac } from "@/components/OilTrac";
import { RecordFiles } from "@/components/portal/RecordFiles";

const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" }) : "—");

const PayButton = ({ record }) => {
  const [loading, setLoading] = useState(false);
  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { record_id: record.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err, "Could not start payment"));
      setLoading(false);
    }
  };
  if (record.payment_status === "paid")
    return (
      <span className="inline-flex items-center gap-1.5 border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-400" data-testid={`record-paid-${record.id}`}>
        <CheckCircle2 size={11} /> Paid
      </span>
    );
  if (record.payment_status !== "unpaid" || !record.cost) return null;
  return (
    <button onClick={pay} disabled={loading} data-testid={`record-pay-${record.id}`}
      className="inline-flex items-center gap-2 bg-amber-600 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-black transition-colors hover:bg-amber-500 disabled:opacity-60">
      {loading ? <Loader2 size={11} className="animate-spin" /> : <CreditCard size={11} />} Pay NZ${record.cost.toLocaleString()}
    </button>
  );
};

const RecordRow = ({ r }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-5" data-testid={`portal-record-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-base font-semibold text-slate-100">{r.title}</h4>
          <span className="font-mono text-xs text-slate-500">{fmtDate(r.service_date)}</span>
        </div>
        <PayButton record={r} />
      </div>
      {r.description && <p className="mt-2 text-sm leading-relaxed text-slate-400">{r.description}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
        {r.odometer_km != null && <span>{r.odometer_km.toLocaleString()} km</span>}
        {r.cost != null && <span className="text-amber-500">NZ${r.cost.toLocaleString()}</span>}
        {r.next_service_due && <span className="flex items-center gap-1.5 text-teal-400"><CalendarClock size={12} /> Next due {fmtDate(r.next_service_due)}</span>}
        {r.oil_change && <span className="flex items-center gap-1.5 text-amber-500"><Droplets size={12} /> Oil {r.oil_grade || "changed"}</span>}
        <button onClick={() => setOpen(!open)} data-testid={`portal-record-files-toggle-${r.id}`} className="flex items-center gap-1.5 text-slate-400 transition-colors hover:text-amber-500">
          <Paperclip size={12} /> {r.file_count} file{r.file_count === 1 ? "" : "s"} <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && <RecordFiles recordId={r.id} />}
    </div>
  );
};

export const VehicleCard = ({ vehicle, index }) => (
  <motion.section
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    className="border border-white/10 bg-[#12151C] p-6 sm:p-8"
    data-testid={`vehicle-card-${vehicle.rego}`}
  >
    <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-5">
      <div className="flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center border border-white/10 bg-[#0D1017] text-amber-500"><Car size={22} /></div>
        <div>
          <div className="text-lg font-bold text-slate-100" data-testid={`vehicle-name-${vehicle.rego}`}>{vehicle.vehicle || "Vehicle"}</div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{vehicle.records.length} service record{vehicle.records.length === 1 ? "" : "s"}</div>
        </div>
      </div>
      <span className="border border-teal-500/40 bg-teal-500/10 px-3 py-1.5 font-mono text-sm tracking-[0.2em] text-teal-400" data-testid={`vehicle-rego-${vehicle.rego}`}>{vehicle.rego}</span>
    </div>
    <OilTrac data={vehicle.oil_trac} />
    <div className="mt-2 divide-y divide-white/5">
      {vehicle.records.map((r) => <RecordRow key={r.id} r={r} />)}
    </div>
  </motion.section>
);
